import { useEffect, useState } from "react";
import {
  TrendingUp, Award, Building2, Activity, Users, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fmtDateTime } from "@/lib/api";
import { Input } from "@/components/ui/input";

const BAND_TONE = {
  Champion: "bg-[#12B76A]/10 text-[#12B76A]",
  Steady:   "bg-[#457b9d]/10 text-[#457b9d]",
  Trial:    "bg-[#F79009]/10 text-[#F79009]",
  Dormant:  "bg-[#66708515] text-[#667085]",
};

const KpiTile = ({ label, value, tone = "steel", Icon = Users }) => {
  const map = {
    steel: "bg-[#a8dadc]/30 text-[#457b9d]",
    green: "bg-[#12B76A]/10 text-[#12B76A]",
    orange: "bg-[#F79009]/10 text-[#F79009]",
    red: "bg-[#e63946]/10 text-[#e63946]",
    muted: "bg-[#66708520] text-[#667085]",
  };
  return (
    <div className="bg-white rounded-xl border border-[#E4E7EC] p-4">
      <div className={`w-9 h-9 rounded-lg grid place-items-center mb-3 ${map[tone]}`}>
        <Icon size={17} strokeWidth={2} />
      </div>
      <div className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold">{label}</div>
      <div className="text-[24px] font-bold text-[#1d3557] leading-tight mt-1 font-mono">{value}</div>
    </div>
  );
};

const FunnelBar = ({ step, count, total }) => {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-[12px] mb-1">
        <span className="text-[#1d3557] font-semibold">{step}</span>
        <span className="font-mono text-[#667085]">{count} · {pct}%</span>
      </div>
      <div className="w-full h-2 bg-[#F1FAEE] rounded-full overflow-hidden">
        <div className="h-full bg-[#e63946] rounded-full transition-all" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

export default function AdminAdoption() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/admin/analytics/adoption-detail")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  if (loading && !data) return <div className="p-6 text-[13px] text-[#667085]">Loading adoption analytics…</div>;
  if (!data) return null;

  const total = data.funnel?.[0]?.count || 0;
  const scores = (data.scores || []).filter((s) =>
    !q.trim() ||
    s.garage_name.toLowerCase().includes(q.toLowerCase()) ||
    (s.city || "").toLowerCase().includes(q.toLowerCase()) ||
    s.band.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="space-y-5" data-testid="admin-adoption-root">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#e63946] mb-1">Growth</div>
          <h1 className="text-[26px] md:text-[30px] font-bold text-[#1d3557] flex items-center gap-2">
            <TrendingUp size={22} className="text-[#457b9d]" /> Adoption Analytics
          </h1>
          <p className="text-[13px] text-[#667085] mt-0.5">
            Cohort retention, feature funnel, and per-garage adoption scores. Generated {fmtDateTime(data.generated_at)}.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="admin-adoption-refresh"
          className="h-9 px-3 rounded-md border border-[#E4E7EC] bg-white text-[13px] font-semibold text-[#1d3557] hover:border-[#457b9d] inline-flex items-center gap-1.5"
        >
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {/* Band summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile label="Champions"  value={data.band_counts?.Champion || 0} tone="green"  Icon={Award} />
        <KpiTile label="Steady"     value={data.band_counts?.Steady || 0}   tone="steel"  Icon={Building2} />
        <KpiTile label="Trial"      value={data.band_counts?.Trial || 0}    tone="orange" Icon={Activity} />
        <KpiTile label="Dormant"    value={data.band_counts?.Dormant || 0}  tone="muted"  Icon={Users} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Cohort table */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-[#E4E7EC] overflow-hidden" data-testid="admin-adoption-cohorts">
          <div className="px-5 py-3.5 border-b border-[#F1FAEE]">
            <h3 className="text-[15px] font-semibold text-[#1d3557]">Signup cohorts — 4-week retention</h3>
            <div className="text-[11px] text-[#667085] mt-0.5">How many garages from each signup week were still active in the next 3 weeks.</div>
          </div>
          {data.cohorts.length === 0 ? (
            <div className="p-6 text-[13px] text-[#667085]">Not enough data yet.</div>
          ) : (
            <table className="w-full text-[13px]">
              <thead className="bg-[#F7F9FC] text-[11px] uppercase tracking-wider text-[#667085]">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Cohort week</th>
                  <th className="text-right px-4 py-3 font-semibold">Size</th>
                  <th className="text-right px-4 py-3 font-semibold">W0</th>
                  <th className="text-right px-4 py-3 font-semibold">W1</th>
                  <th className="text-right px-4 py-3 font-semibold">W2</th>
                  <th className="text-right px-4 py-3 font-semibold">W3</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F1FAEE]">
                {data.cohorts.map((c) => (
                  <tr key={c.cohort_week} data-testid={`admin-cohort-row-${c.cohort_week}`}>
                    <td className="px-4 py-3 font-mono font-semibold text-[#1d3557]">{c.cohort_week}</td>
                    <td className="px-4 py-3 text-right font-mono text-[#1d3557]">{c.size}</td>
                    {[0, 1, 2, 3].map((w) => {
                      const v = c.retention[w] || 0;
                      const pct = c.size > 0 ? Math.round((v / c.size) * 100) : 0;
                      return (
                        <td key={w} className="px-4 py-3 text-right font-mono" style={{
                          background: `rgba(230,57,70,${Math.min(pct / 200, 0.35)})`,
                        }}>
                          <div className="text-[#1d3557] font-semibold">{v}</div>
                          <div className="text-[10px] text-[#667085]">{pct}%</div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Funnel */}
        <div className="bg-white rounded-xl border border-[#E4E7EC] p-5" data-testid="admin-adoption-funnel">
          <h3 className="text-[15px] font-semibold text-[#1d3557]">Feature funnel</h3>
          <div className="text-[11px] text-[#667085] mt-0.5">Unique garages that have tried each feature (all-time).</div>
          <div className="mt-4 space-y-3">
            {(data.funnel || []).map((f) => (
              <FunnelBar key={f.step} step={f.step} count={f.count} total={total} />
            ))}
          </div>
        </div>
      </div>

      {/* Per-garage scores */}
      <div className="bg-white rounded-xl border border-[#E4E7EC] overflow-hidden" data-testid="admin-adoption-scores">
        <div className="px-5 py-3.5 border-b border-[#F1FAEE] flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="text-[15px] font-semibold text-[#1d3557]">Per-garage adoption score (last 30 days)</h3>
            <div className="text-[11px] text-[#667085] mt-0.5">Weighted composite: Job cards = 5, Invoices = 5, Estimates = 4, Enquiries + Inspections = 3.</div>
          </div>
          <Input
            data-testid="admin-adoption-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter garage or band…"
            className="max-w-xs h-9 rounded-md text-[13px]"
          />
        </div>
        <table className="w-full text-[13px]">
          <thead className="bg-[#F7F9FC] text-[11px] uppercase tracking-wider text-[#667085]">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Garage</th>
              <th className="text-right px-4 py-3 font-semibold">Score</th>
              <th className="text-left px-4 py-3 font-semibold">Band</th>
              <th className="text-right px-4 py-3 font-semibold">JC</th>
              <th className="text-right px-4 py-3 font-semibold">Est</th>
              <th className="text-right px-4 py-3 font-semibold">Inv</th>
              <th className="text-right px-4 py-3 font-semibold">Enq</th>
              <th className="text-right px-4 py-3 font-semibold">Insp</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F1FAEE]">
            {scores.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-[13px] text-[#667085]">No garages match this filter.</td></tr>
            )}
            {scores.map((s) => (
              <tr key={s.garage_id} data-testid={`admin-adoption-row-${s.garage_id}`} className="hover:bg-[#F7F9FC]">
                <td className="px-4 py-3">
                  <div className="text-[#1d3557] font-semibold">{s.garage_name}</div>
                  <div className="text-[11px] text-[#667085]">{s.city || "—"}</div>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-2">
                    <div className="w-24 h-1.5 bg-[#F1FAEE] rounded-full overflow-hidden">
                      <div className="h-full bg-[#e63946] rounded-full" style={{ width: `${s.score}%` }} />
                    </div>
                    <span className="font-mono text-[#1d3557] font-bold">{s.score}</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${BAND_TONE[s.band] || BAND_TONE.Dormant}`}>
                    {s.band}
                  </span>
                </td>
                <td className="px-4 py-3 text-right font-mono text-[#1d3557]">{s.usage_30d?.job_cards ?? 0}</td>
                <td className="px-4 py-3 text-right font-mono text-[#667085]">{s.usage_30d?.estimates ?? 0}</td>
                <td className="px-4 py-3 text-right font-mono text-[#667085]">{s.usage_30d?.invoices ?? 0}</td>
                <td className="px-4 py-3 text-right font-mono text-[#667085]">{s.usage_30d?.enquiries ?? 0}</td>
                <td className="px-4 py-3 text-right font-mono text-[#667085]">{s.usage_30d?.inspections ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
